import type { JSX } from 'react';

/**
 * The small slice of Markdown that question and assignment text actually uses.
 *
 * Deliberately not a Markdown library and deliberately not `dangerouslySetInnerHTML`: the text
 * comes from a teacher, but it renders in the browser of every student in the group, so the safe
 * path is to build React elements and never produce HTML at all. Supported: paragraphs, fenced
 * code, `inline code`, **bold** and *italic*.
 */

type Token = { kind: 'text' | 'code' | 'bold' | 'italic'; value: string };

const INLINE = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/u;

function tokenize(line: string): Token[] {
  return line
    .split(INLINE)
    .filter((part) => part.length > 0)
    .map((part) => {
      if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
        return { kind: 'code' as const, value: part.slice(1, -1) };
      }
      if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
        return { kind: 'bold' as const, value: part.slice(2, -2) };
      }
      if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
        return { kind: 'italic' as const, value: part.slice(1, -1) };
      }
      return { kind: 'text' as const, value: part };
    });
}

function Inline({ line }: { line: string }): JSX.Element {
  return (
    <>
      {tokenize(line).map((token, index) => {
        // Code is literal all the way down; emphasis is not, so `x` inside **…** still renders.
        if (token.kind === 'code') return <code key={index}>{token.value}</code>;
        if (token.kind === 'bold') {
          return (
            <strong key={index}>
              <Inline line={token.value} />
            </strong>
          );
        }
        if (token.kind === 'italic') {
          return (
            <em key={index}>
              <Inline line={token.value} />
            </em>
          );
        }
        return <span key={index}>{token.value}</span>;
      })}
    </>
  );
}

export function Markup({ text }: { text: string }): JSX.Element {
  const blocks = text.split(/```/u);

  return (
    <div className="markup">
      {blocks.map((block, index) =>
        // Odd indices are the inside of a fence, so a code sample keeps its own line breaks.
        index % 2 === 1 ? (
          <pre key={index}>
            <code>{block.replace(/^\n/u, '')}</code>
          </pre>
        ) : (
          block
            .split('\n')
            .filter((line) => line.trim().length > 0)
            .map((line, lineIndex) => (
              <p key={`${index}-${lineIndex}`}>
                <Inline line={line} />
              </p>
            ))
        ),
      )}
    </div>
  );
}
